import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import { toast } from "react-toastify";
import { apiService } from '../../../services/api';
import {
  CategoriaFinanceira,
  CreateCategoriaFinanceiraData,
  UpdateCategoriaFinanceiraData,
  CategoriaFinanceiraFilters
} from '../../../types/financeiro';
import ScrollableTable from '../contracts/components/ScrollableTable';

const CategoriasTipos = [
  { value: 'RECEITA', label: 'Receita' },
  { value: 'DESPESA', label: 'Despesa' }
];

const CategoriasFinanceiras = () => {
  // Estados principais
  const [categorias, setCategorias] = useState<CategoriaFinanceira[]>([]);
  const [filteredCategorias, setFilteredCategorias] = useState<CategoriaFinanceira[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  // Estados para formulários
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<CategoriaFinanceira | null>(null);
  const [formData, setFormData] = useState<CreateCategoriaFinanceiraData>({
    nome: '',
    tipo: 'RECEITA',
    ativa: true
  });
  const [updateFormData, setUpdateFormData] = useState<UpdateCategoriaFinanceiraData>({});

  // Estados para controle
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Estados para controle de erros nos modais
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // Função para fechar modal programaticamente
  const closeModal = (modalId: string) => {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      // Remove classes do Bootstrap
      modalElement.classList.remove('show');
      modalElement.style.display = 'none';
      modalElement.setAttribute('aria-hidden', 'true');
      modalElement.removeAttribute('aria-modal');
      modalElement.removeAttribute('role');

      // Remove backdrop
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.remove();
      }

      // Remove classe do body
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    }
  };

  // Buscar categorias
  const fetchCategorias = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getCategoriaFinanceira();

      if (response.success && response.data) {
        let categoriasArray: CategoriaFinanceira[] = [];

        if (Array.isArray(response.data)) {
          categoriasArray = response.data as CategoriaFinanceira[];
        } else {
          const responseData = response.data as any;
          categoriasArray = responseData.data || [];
        }

        const categoriasComKey = categoriasArray.map((categoria: any) => ({
          ...categoria,
          key: categoria.id.toString()
        }));

        setCategorias(categoriasComKey);
        setFilteredCategorias(categoriasComKey);
      } else {
        console.error('Resposta sem sucesso ou sem dados:', response);
        setError('Não foi possível obter os dados das categorias');
      }
    } catch (err) {
      console.error('Erro ao buscar categorias:', err);
      setError('Erro ao carregar dados das categorias');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategorias();
  }, []);

  // Filtro de pesquisa com debounce
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredCategorias(categorias);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');

      const filtered = categorias.filter(categoria => {
        const searchableFields = [
          categoria.nome,
          categoria.tipo,
          categoria.descricao,
          categoria.ativa ? 'Ativo' : 'Inativo'
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredCategorias(filtered);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchText, categorias]);

  // Função para limpar pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para editar categoria
  const handleEditCategoria = async (id: number) => {
    try {
      setEditError(null);
      const categoria = categorias.find(c => c.id === id);
      if (categoria) {
        setCategoriaSelecionada(categoria);
        setUpdateFormData({
          nome: categoria.nome,
          tipo: categoria.tipo,
          descricao: categoria.descricao,
          ativa: categoria.ativa
        });
      }
    } catch (err) {
      console.error('Erro ao preparar edição:', err);
      setEditError('Erro ao carregar dados da categoria');
    }
  };

  // Função para submeter edição
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoriaSelecionada) return;

    try {
      setIsLoading(true);
      setEditError(null);

      const response = await apiService.updateCategoriaFinanceira(categoriaSelecionada.id, updateFormData);

      if (response.success) {
        const closeBtn = document.querySelector('#offcanvas_edit .btn-close') as HTMLElement;
        if (closeBtn) closeBtn.click();

        await fetchCategorias();
        toast.success('Categoria atualizada com sucesso!');
      } else {
        setEditError('Erro ao atualizar categoria: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao atualizar categoria:', err);
      setEditError('Erro ao atualizar categoria: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsLoading(false);
    }
  };

  // Função para criar categoria
  const handleCreateCategoria = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      const response = await apiService.createCategoriaFinanceira(formData);

      if (response.success) {
        const closeBtn = document.querySelector('#offcanvas_add .btn-close') as HTMLElement;
        if (closeBtn) closeBtn.click();

        // Resetar formulário
        setFormData({
          nome: '',
          tipo: 'RECEITA',
          ativa: true
        });

        await fetchCategorias();
        toast.success('Categoria criada com sucesso!');
      } else {
        setError('Erro ao criar categoria: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao criar categoria:', err);
      setError('Erro ao criar categoria: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsLoading(false);
    }
  };

  // Função para deletar categoria
  const handleDeleteCategoria = (id: number) => {
    setDeleteId(id);
    setDeleteError(null);
  };

  // Confirmar exclusão
  const confirmDeleteCategoria = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);

      const response = await apiService.deleteCategoriaFinanceira(deleteId);

      if (response.success) {
        closeModal('delete_categoria');
        await fetchCategorias();
        toast.success("Categoria excluída com sucesso!");
        setDeleteId(null);
      } else {
        setDeleteError('Erro ao excluir categoria: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao excluir categoria:', err);
      setDeleteError('Erro ao excluir categoria: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Função para manipular mudanças no formulário
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'ativa' ? (value === '1' ? true : false) : value
    });
  };

  // Função para manipular mudanças no formulário de edição
  const handleUpdateFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUpdateFormData({
      ...updateFormData,
      [name]: name === 'ativa' ? (value === '1' ? true : false) : value
    });
  };

  // Definir as colunas da tabela
  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      sorter: true,
      render: (text: string, record: CategoriaFinanceira) => (
        <div className="d-flex flex-column">
          <span className="fw-semibold">{text}</span>
          {record.descricao && (
            <small className="text-muted">{record.descricao}</small>
          )}
        </div>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      sorter: true,
      render: (tipo: string) => (
        <span className={`badge badge-pill ${tipo === 'RECEITA' ? 'bg-success' : 'bg-danger'}`}>
          {tipo === 'RECEITA' ? 'Receita' : 'Despesa'}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "ativa",
      key: "ativa",
      sorter: true,
      render: (ativa: boolean) => (
        <span className={`badge badge-pill ${ativa ? 'bg-danger' : 'bg-success'}`}>
          {ativa ? 'Inativo' : 'Ativo'}
        </span>
      ),
    },
    {
      title: "Ações",
      dataIndex: "acoes",
      key: "acoes",
      render: (text: string, record: CategoriaFinanceira) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            data-bs-toggle="offcanvas"
            data-bs-target="#offcanvas_edit"
            onClick={() => handleEditCategoria(record.id)}
          >
            <i className="ti ti-edit"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            data-bs-toggle="modal"
            data-bs-target="#delete_categoria"
            onClick={() => handleDeleteCategoria(record.id)}
          >
            <i className="ti ti-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Categorias Financeiras
                      <span className="count-title">{filteredCategorias.length} categorias</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Categorias"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add"
                        >
                          <i className="ti ti-plus me-2"></i>
                          Nova Categoria
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Tabela de Categorias */}
                  {isLoading ? (
                    <div className="text-center p-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="mt-2">Carregando categorias...</p>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  ) : !isSearching && searchText && filteredCategorias.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="ti ti-search-off fs-3 text-muted"></i>
                      </div>
                      <h5 className="text-muted">Nenhuma categoria encontrada</h5>
                      <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                      <button
                        className="btn btn-outline-primary mt-2"
                        onClick={handleClearSearch}
                      >
                        <i className="ti ti-x me-1"></i>
                        Limpar pesquisa
                      </button>
                    </div>
                  ) : (
                    <ScrollableTable dataSource={filteredCategorias} columns={columns} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Adicionar Categoria */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_add"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Nova Categoria</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateCategoria}>
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Nome <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="nome"
                    value={formData.nome}
                    onChange={handleFormChange}
                    required
                    placeholder="Nome da categoria"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Tipo <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleFormChange}
                    required
                  >
                    {CategoriasTipos.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="ativa"
                    value={formData.ativa ? '1' : '0'}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="1">Ativo</option>
                    <option value="0">Inativo</option>
                  </select>
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Descrição
                  </label>
                  <textarea
                    className="form-control"
                    name="descricao"
                    value={formData.descricao || ''}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder="Descrição da categoria..."
                  />
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                data-bs-dismiss="offcanvas"
                className="btn btn-light me-2"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Criando...
                  </>
                ) : (
                  'Criar Categoria'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal Editar Categoria */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_edit"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Editar Categoria</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {isLoading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmitEdit}>
              <div className="row">
                {editError && (
                  <div className="alert alert-danger mb-3" role="alert">
                    <i className="ti ti-alert-circle me-2"></i>
                    {editError}
                  </div>
                )}
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Nome <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="nome"
                      value={updateFormData.nome || ''}
                      onChange={handleUpdateFormChange}
                      required
                      placeholder="Nome da categoria"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Tipo <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      name="tipo"
                      value={updateFormData.tipo || 'RECEITA'}
                      onChange={handleUpdateFormChange}
                      required
                    >
                      {CategoriasTipos.map(tipo => (
                        <option key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      name="ativa"
                      value={updateFormData.ativa ? '1' : '0'}
                      onChange={handleUpdateFormChange}
                      required
                    >
                      <option value="1">Ativo</option>
                      <option value="0">Inativo</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Descrição
                    </label>
                    <textarea
                      className="form-control"
                      name="descricao"
                      value={updateFormData.descricao || ''}
                      onChange={handleUpdateFormChange}
                      rows={3}
                      placeholder="Descrição da categoria..."
                    />
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-end">
                <button
                  type="button"
                  data-bs-dismiss="offcanvas"
                  className="btn btn-light me-2"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Salvando...
                    </>
                  ) : (
                    'Salvar Alterações'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Modal Confirmar Exclusão */}
      <div className="modal fade" id="delete_categoria" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Excluir Categoria?</h4>
                <p className="mb-0">
                  Tem certeza de que deseja excluir <br /> esta categoria?
                </p>

                {/* Exibir erro no modal se houver */}
                {deleteError && (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    <i className="ti ti-alert-circle me-2"></i>
                    {deleteError}
                  </div>
                )}

                <div className="d-flex align-items-center justify-content-center mt-4">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    onClick={() => setDeleteError(null)}
                  >
                    Cancelar
                  </Link>
                  <button
                    onClick={confirmDeleteCategoria}
                    className="btn btn-danger"
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Excluindo...
                      </>
                    ) : (
                      'Sim, Excluir'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriasFinanceiras;